import { useEffect, useRef } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useImpersonation } from '../../hooks/useImpersonation';
import { TenantType } from '../../types/tenant.types';
import { useUnifiedAuth } from '../UnifiedAuthProvider/UnifiedAuthProvider';

// `??` so an intentionally-empty VITE_API_ENDPOINT (behind the worker proxy) stays same-origin.
const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT ?? 'http://localhost:4200';
const SCRIPT_SRC = '/revvly-events.js';
const SELF_TOKEN_ENDPOINT = '/api/v1/product-events/self-token';

interface RevvlyEventsGlobal {
  init(options: Record<string, unknown>): void;
  flush(): Promise<void>;
  shutdown(): void;
}

/** How long a switch waits for the last events to go out before shutting the script down anyway. */
const FLUSH_GRACE_MS = 1500;

/**
 * The script being wound down: it sends what it still holds, then stops. The next start waits for
 * it, because two copies running at once would each patch the browser's history functions, and the
 * first one stopping would undo the second's patch.
 */
let stopping: Promise<void> = Promise.resolve();

function stop(events: RevvlyEventsGlobal): Promise<void> {
  const grace = new Promise<void>((resolve) => setTimeout(resolve, FLUSH_GRACE_MS));
  const sent = Promise.resolve()
    .then(() => events.flush())
    .catch(() => undefined);
  return Promise.race([sent, grace]).then(() => {
    try {
      events.shutdown();
    } catch {
      /* nothing to undo */
    }
  });
}

interface SelfTokenAnswer {
  token?: string;
  skipped?: string;
}

const sdk = (): RevvlyEventsGlobal | undefined =>
  (window as unknown as { RevvlyEvents?: RevvlyEventsGlobal }).RevvlyEvents;

/**
 * Loads the tracking script. `shutdown()` removes the script's global, so each start runs the file
 * again; the browser serves it from cache after the first time.
 */
function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      script.remove();
      resolve();
    };
    script.onerror = () => {
      script.remove();
      reject(new Error('The usage script could not be loaded'));
    };
    document.head.appendChild(script);
  });
}

/**
 * Reports what a signed-in person does in this dashboard, so the Revvly Master workspace can see
 * how each client uses Revvly (docs/architecture/PRODUCT_USAGE.md, "Revvly's own usage").
 *
 * It is the product tracking exactly as a customer uses it: the same script, started with a
 * `getToken` that asks Revvly's backend for a token (here from the person's login rather than an
 * API key the page never sees), and events sent to the ordinary browser route. Nothing is special
 * to this surface except where the token comes from. It renders nothing, and nothing here can
 * break the dashboard: every failure is swallowed.
 *
 * Not started in the Master workspace or while impersonating a client. The server refuses to mint
 * a token for the Master workspace, or when tracking is not set up; this is only so the Master
 * account does not ask. Someone with a seat in both Master and a client's workspace is tracked in
 * the client's. Honours Do Not Track.
 * Switched off for a build with `VITE_PRODUCT_USAGE_SELF_TRACKING=false`.
 */
export function ProductUsageTracker(): null {
  const { isAuthenticated, getAccessTokenSilently } = useUnifiedAuth();
  const { selectedTenant } = useTenant();
  // A Master person looking at a client's workspace is not that client's user.
  const { isImpersonating } = useImpersonation();
  const tenantId = selectedTenant?.id;
  const tenantType = selectedTenant?.type;

  // The token getter changes identity with the auth provider's renders; the script must not be
  // torn down and restarted (losing what it has queued) each time.
  const getToken = useRef(getAccessTokenSilently);
  getToken.current = getAccessTokenSilently;

  useEffect(() => {
    if (import.meta.env.VITE_PRODUCT_USAGE_SELF_TRACKING === 'false') return undefined;
    if (!isAuthenticated || !tenantId || !tenantType) return undefined;
    if (tenantType === TenantType.MASTER || isImpersonating) return undefined;

    let cancelled = false;
    const running: { events: RevvlyEventsGlobal | null } = { events: null };

    /** One token for this person in this workspace; null when they are not tracked. */
    const fetchToken = async (): Promise<string | null> => {
      const jwt = await getToken.current();
      const response = await fetch(
        `${API_ENDPOINT || window.location.origin}${SELF_TOKEN_ENDPOINT}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${jwt}`, 'X-Tenant-ID': tenantId },
        },
      );
      if (!response.ok) throw new Error(`Token request failed (${response.status})`);
      const answer = (await response.json()) as SelfTokenAnswer;
      return answer.token ?? null;
    };

    // Ask once before loading anything: a person who is not tracked (the Master account's own
    // people, a workspace that is not a client, tracking not set up) never loads the script.
    let first: string | null = null;
    fetchToken()
      .then((token) => {
        first = token;
        return token ? stopping.then(() => (cancelled ? undefined : loadScript())) : undefined;
      })
      .then(() => {
        const events = sdk();
        if (cancelled || !events || !first) return;
        let pending: string | null = first;
        events.init({
          apiUrl: API_ENDPOINT || window.location.origin,
          // The first call is answered from what was just fetched; the script calls again before
          // the token expires.
          getToken: async () => {
            if (pending) {
              const token = pending;
              pending = null;
              return token;
            }
            const token = await fetchToken();
            if (!token) throw new Error('Not tracked');
            return token;
          },
          respectDoNotTrack: true,
        });
        running.events = events;
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      // A tenant switch or a sign-out ends this person's tracking in that workspace. What the script
      // still holds (up to a few seconds of page views) is sent first, in that workspace.
      if (running.events) stopping = stop(running.events);
    };
  }, [isAuthenticated, tenantId, tenantType, isImpersonating]);

  return null;
}
